import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import {
  useAddProgramMutation,
  useUpdateProgramMutation,
} from "../api/programsApi";
import { Program, ProgramFormData, ProgramCategory } from "../types/program";

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  program?: Program | null;
}

const AddEditProgram: React.FC<Props> = ({
  open,
  setModalOpen,
  onSuccess,
  program,
}) => {
  const [addProgram, { isLoading: isAdding }] = useAddProgramMutation();
  const [updateProgram, { isLoading: isUpdating }] = useUpdateProgramMutation();

  const [formData, setFormData] = useState<ProgramFormData>({
    name: "",
    description: "",
    objectives: [""],
    achievements: [""],
    category_ids: [],
    image: null,
  });

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [categories, setCategories] = useState<ProgramCategory[]>([]);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  /** ✅ Fetch categories */
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          "https://api.cond-net.org/api/user/program-categories"
        );
        const data = await response.json();
        if (data.success) {
          setCategories(data.data);
        } else {
          setSubmissionError("Failed to load categories");
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        setSubmissionError("Failed to load categories");
      }
    };
    fetchCategories();
  }, []);

  /** ✅ Pre-fill form when editing */
  useEffect(() => {
    if (program) {
      setFormData({
        name: program.name || "",
        description: program.description || "",
        objectives: program.objectives?.map((obj) => obj.program_objective) || [
          "",
        ],
        achievements: program.achievements?.map(
          (ach) => ach.program_achievement
        ) || [""],
        category_ids:
          program.program_categories?.map((cat) => cat.id.toString()) || [],
        image: null,
      });

      setImagePreview(program.image_url || null);
    } else {
      setFormData({
        name: "",
        description: "",
        objectives: [""],
        achievements: [""],
        category_ids: [],
        image: null,
      });
      setImagePreview(null);
    }
  }, [program]);

  /** ✅ Handle text & select inputs */
  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /** ✅ Handle objectives & achievements arrays */
  const handleArrayChange = (
    field: "objectives" | "achievements",
    index: number,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].map((item, i) => (i === index ? value : item)),
    }));
  };

  const addArrayItem = (field: "objectives" | "achievements") => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], ""],
    }));
  };

  const removeArrayItem = (
    field: "objectives" | "achievements",
    index: number
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  /** ✅ Handle image selection */
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, image: file }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  /** ✅ Submit form */
  const handleSubmit = useCallback(async () => {
    if (!formData.name.trim()) {
      setSubmissionError("Name is required");
      return;
    }

    setIsSaving(true);
    setSubmissionError(null);

    try {
      if (program) {
        await updateProgram({
          id: program.id.toString(),
          formData,
        }).unwrap();
      } else {
        await addProgram(formData).unwrap();
      }

      setModalOpen(false);
      onSuccess?.();
    } catch (error: any) {
      setSubmissionError(
        error?.data?.message || "An error occurred while saving the program"
      );
      console.error("Submission error:", error);
    } finally {
      setIsSaving(false);
    }
  }, [formData, program, addProgram, updateProgram, setModalOpen, onSuccess]);

  /** ✅ Close modal */
  const handleClose = () => {
    setModalOpen(false);
    setSubmissionError(null);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{program ? "Edit Program" : "Add New Program"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {submissionError && <Alert severity="error">{submissionError}</Alert>}

          {/* ✅ Image Upload */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Program Image
            </Typography>
            {imagePreview && (
              <Box
                sx={{
                  mb: 1,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <img
                  src={imagePreview}
                  alt="Program preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 200,
                    borderRadius: 8,
                    objectFit: "cover",
                  }}
                />
              </Box>
            )}
            <Button variant="outlined" component="label">
              {formData.image ? "Change Image" : "Upload Image"}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageChange}
              />
            </Button>
          </Box>

          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Overview of the pillar"
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            fullWidth
            multiline
            rows={3}
          />

          {/* ✅ Category select */}
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={formData.category_ids[0] || ""}
              onChange={(e) =>
                handleInputChange("category_ids", [e.target.value])
              }
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id.toString()}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* ✅ Objectives */}
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2">
                What the pillar aims to achieve
              </Typography>
              <IconButton
                size="small"
                onClick={() => addArrayItem("objectives")}
              >
                <Add />
              </IconButton>
            </Box>
            {formData.objectives.map((objective, index) => (
              <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
                <TextField
                  value={objective}
                  onChange={(e) =>
                    handleArrayChange("objectives", index, e.target.value)
                  }
                  fullWidth
                  placeholder="Enter objective"
                />
                {formData.objectives.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => removeArrayItem("objectives", index)}
                  >
                    <Remove />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>

          {/* ✅ Achievements */}
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2">
                Result Highlight (Key outcomes)
              </Typography>
              <IconButton
                size="small"
                onClick={() => addArrayItem("achievements")}
              >
                <Add />
              </IconButton>
            </Box>
            {formData.achievements.map((achievement, index) => (
              <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
                <TextField
                  value={achievement}
                  onChange={(e) =>
                    handleArrayChange("achievements", index, e.target.value)
                  }
                  fullWidth
                  placeholder="Enter key outcome"
                />
                {formData.achievements.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => removeArrayItem("achievements", index)}
                  >
                    <Remove />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSaving || isAdding || isUpdating}
          startIcon={isSaving ? <CircularProgress size={20} /> : null}
        >
          {isSaving ? "Saving..." : program ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditProgram;
